/**
 * Utility functions for handling large integer IDs
 * JavaScript has precision issues with large integers (beyond 2^53 - 1)
 * These functions ensure we always handle large IDs as strings to avoid precision loss
 */

/**
 * Ensure a large ID is handled as a string to avoid precision issues
 * @param {any} id - The ID that might be a large number
 * @returns {string} - String representation of the ID
 */
export function ensureStringId(id) {
  if (id === null || id === undefined) return '';
  
  // If it's already a string, return it unchanged
  if (typeof id === 'string') return id;
  
  // If it's a number, convert to string with care for large numbers
  if (typeof id === 'number') {
    // For large numbers beyond safe integer range, handle specially
    if (id > Number.MAX_SAFE_INTEGER || id < Number.MIN_SAFE_INTEGER) {
      console.warn(`Converting potentially unsafe large number to string: ${id}`);
      
      // Large IDs from the backend might suffer from precision loss in JavaScript
      // To mitigate this, we'll add a warning so developers can address it systematically
      // rather than hardcoding specific IDs
      console.warn(`Warning: ID ${id} exceeds JavaScript's safe integer range.`);
      console.warn(`Consider updating the API to return IDs as strings instead of numbers.`);
    }
    
    return String(id);
  }
  
  // For other types, do basic string conversion
  return String(id);
}

/**
 * Compare two potentially large IDs safely by converting to strings
 * @param {any} id1 - First ID to compare
 * @param {any} id2 - Second ID to compare
 * @returns {boolean} - True if IDs match
 */
export function compareIds(id1, id2) {
  return ensureStringId(id1) === ensureStringId(id2);
}

/**
 * Process an API response to ensure large IDs are preserved as strings
 * @param {Object} data - API response data
 * @returns {Object} - Processed data with string IDs
 */
export function processApiResponse(data) {
  if (!data) return data;
  
  // Process arrays
  if (Array.isArray(data)) {
    return data.map(item => processApiResponse(item));
  }
  
  // Process objects
  if (typeof data === 'object') {
    const result = {};
    
    // List of properties that should always be handled as strings
    const idProperties = [
      'quotation_id', 'id', 'user_id', 'specification_id', 
      'requisition_id', 'procurement_id', 'purchase_order_id',
      'quotation_company_code', 'company_code', 'supplier_id',
      'workflow_id', 'approval_id', 'amendment_id'
    ];
    
    // Copy all properties, ensuring ID properties are strings
    for (const key in data) {
      if (idProperties.includes(key)) {
        // Always convert ID properties to strings, regardless of their current type or size
        result[key] = data[key] !== null && data[key] !== undefined ? ensureStringId(data[key]) : data[key];
      } else if (typeof data[key] === 'object' && data[key] !== null) {
        // Recursively process nested objects and arrays
        result[key] = processApiResponse(data[key]);
      } else {
        // Copy other values as is
        result[key] = data[key];
      }
    }
    
    return result;
  }
  
  // Return primitive values as is
  return data;
}
